import { getLocale, t, useI18n } from '../../i18n';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../supabaseClient';
import './GuardLicencesPage.css';

type LicenceStatus = 'pending_review' | 'approved' | 'rejected' | 'expired' | 'revoked';
type LicenceAuthority = 'PSA' | 'SIA' | 'OTHER';
type ExpiryFlag = 'valid' | 'expiring_soon' | 'expired';
type LicenceView = 'pending_review' | 'all';
type LicenceSort = 'submitted_desc' | 'submitted_asc' | 'expiry_asc' | 'expiry_desc' | 'guard_asc';

interface Props {
  initialSelectedLicenceId?: string | null;
}

interface FilterOption {
  value: string;
  label: string;
}

interface GuardLicence {
  id: string;
  company_id: string;
  guard_id: string;
  licence_holder_name: string;
  licence_number: string;
  licence_type_id: string | null;
  licence_type_name: string | null;
  authority: LicenceAuthority;
  country_code: string;
  issue_date: string | null;
  expiry_date: string;
  status: LicenceStatus;
  document_storage_path: string | null;
  submitted_at: string;
  guard_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  reviewer_notes: string | null;
  guard_full_name: string | null;
  guard_phone: string | null;
  reviewer_full_name: string | null;
  expiry_flag: ExpiryFlag;
}

const PAGE_SIZE = 25;
const FETCH_BATCH_SIZE = 1000;
const EXPIRING_SOON_DAYS = 30;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

const LICENCE_STATUSES: LicenceStatus[] = ['pending_review', 'approved', 'rejected', 'expired', 'revoked'];
const LICENCE_AUTHORITIES: LicenceAuthority[] = ['PSA', 'SIA', 'OTHER'];

const buildAuthorityUrl = (authority: LicenceAuthority): string => {
  switch (authority) {
    case 'PSA':
      return 'https://www.psa-gov.ie/psa-registered-employees/';
    case 'SIA':
      return 'https://services.sia.homeoffice.gov.uk/rolh/';
    default:
      return '';
  }
};

const getAuthorityPortalLabel = (authority: LicenceAuthority): string => {
  switch (authority) {
    case 'SIA': return t('SIA Portal');
    case 'PSA': return t('PSA Portal');
    default: return t('Authority Portal');
  }
};

const getAuthorityLabel = (authority: LicenceAuthority): string =>
  authority === 'OTHER' ? t('Other') : authority;

const getStatusLabel = (status: LicenceStatus): string => {
  switch (status) {
    case 'pending_review': return t('Pending Review');
    case 'approved': return t('Approved||licence');
    case 'rejected': return t('Rejected||licence');
    case 'expired': return t('Expired||licence');
    case 'revoked': return t('Revoked||licence');
    default: return status;
  }
};

const getStatusChipClass = (status: LicenceStatus): string => {
  switch (status) {
    case 'pending_review': return 'status-chip status-chip--warning';
    case 'approved': return 'status-chip status-chip--success';
    case 'rejected': return 'status-chip status-chip--error';
    default: return 'status-chip status-chip--neutral';
  }
};

// A date-only value is a calendar day, so read it as local midnight rather
// than UTC midnight.
const parseDateValue = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const parsed = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (d: string | null | undefined) =>
  parseDateValue(d)?.toLocaleDateString(getLocale()) ?? '—';

const formatDateTime = (d: string | null | undefined) =>
  parseDateValue(d)?.toLocaleString(getLocale()) ?? '—';

const getDaysUntil = (dateStr: string): number | null => {
  const date = parseDateValue(dateStr);
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((date.getTime() - today.getTime()) / MS_PER_DAY);
};

const getExpiryFlag = (expiryDate: string): ExpiryFlag => {
  const days = getDaysUntil(expiryDate);
  if (days === null) return 'valid';
  if (days < 0) return 'expired';
  return days < EXPIRING_SOON_DAYS ? 'expiring_soon' : 'valid';
};

const getExpiryTimingLabel = (expiryDate: string): string => {
  const days = getDaysUntil(expiryDate);
  if (days === null) return '';
  if (days > 1) return t('{count} days remaining', { count: days });
  if (days === 1) return t('1 day remaining');
  if (days === 0) return t('Expires today');
  if (days === -1) return t('Expired 1 day ago');
  return t('Expired {count} days ago', { count: Math.abs(days) });
};

/* expire_guard_licences() moves an approved licence to expired once its date
   has passed. Until that sweep has run the row still says approved, so the
   page reads the date itself — the same call the guard profile and the Ops
   Centre make — and the Expired filter, count and badge all agree. */
const getEffectiveStatus = (licence: Pick<GuardLicence, 'status' | 'expiry_flag'>): LicenceStatus =>
  licence.status === 'approved' && licence.expiry_flag === 'expired' ? 'expired' : licence.status;

// Case- and accent-insensitive, so "sean" finds "Seán".
const normalizeSearch = (value: string): string =>
  value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const getTime = (value: string | null | undefined): number => parseDateValue(value)?.getTime() ?? 0;

const compareNames = (left: GuardLicence, right: GuardLicence): number =>
  (left.guard_full_name ?? '').localeCompare(right.guard_full_name ?? '', getLocale(), { sensitivity: 'base' });

const SORTERS: Record<LicenceSort, (left: GuardLicence, right: GuardLicence) => number> = {
  submitted_desc: (left, right) => getTime(right.submitted_at) - getTime(left.submitted_at),
  submitted_asc: (left, right) => getTime(left.submitted_at) - getTime(right.submitted_at),
  expiry_asc: (left, right) => getTime(left.expiry_date) - getTime(right.expiry_date),
  expiry_desc: (left, right) => getTime(right.expiry_date) - getTime(left.expiry_date),
  guard_asc: compareNames,
};

export default function GuardLicencesPage({ initialSelectedLicenceId = null }: Props) {
  useI18n();
  const [licences, setLicences] = useState<GuardLicence[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);
  const [view, setView] = useState<LicenceView>(initialSelectedLicenceId ? 'all' : 'pending_review');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedAuthority, setSelectedAuthority] = useState('');
  const [selectedExpiry, setSelectedExpiry] = useState('');
  const [sortBy, setSortBy] = useState<LicenceSort>('submitted_desc');
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [copiedNumber, setCopiedNumber] = useState(false);
  const [hasAppliedInitialSelection, setHasAppliedInitialSelection] = useState(false);

  // Every licence is loaded once; the views, filters, search and counts are
  // all worked out from that one list, so switching between them is instant.
  useEffect(() => {
    let cancelled = false;

    const loadLicences = async () => {
      setIsLoading(true);
      setErrorMessage('');
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', user.id)
          .single();

        if (!profile) return;

        const companyId = (profile as { company_id: string }).company_id;

        // Read in batches: a single request stops at the API's row cap, which
        // would quietly leave licences out of the counts.
        const rows: any[] = [];
        for (let from = 0; ; from += FETCH_BATCH_SIZE) {
          const { data, error } = await supabase
            .from('guard_licences')
            .select(`
              *,
              licence_type:licence_types!guard_licences_licence_type_id_fkey(name),
              guard:profiles!guard_licences_guard_id_fkey(full_name, phone),
              reviewer:profiles!guard_licences_reviewed_by_fkey(full_name)
            `)
            .eq('company_id', companyId)
            .order('submitted_at', { ascending: false })
            .order('id', { ascending: true })
            .range(from, from + FETCH_BATCH_SIZE - 1);
          if (error) throw error;
          rows.push(...(data ?? []));
          if ((data ?? []).length < FETCH_BATCH_SIZE) break;
        }
        if (cancelled) return;

        const mapped: GuardLicence[] = rows.map((row: any) => ({
          ...row,
          licence_type_name: row.licence_type?.name ?? null,
          guard_full_name: row.guard?.full_name ?? null,
          guard_phone: row.guard?.phone ?? null,
          reviewer_full_name: row.reviewer?.full_name ?? null,
          expiry_flag: getExpiryFlag(row.expiry_date),
        }));
        setLicences(mapped);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading licences:', (err as Error).message);
          setErrorMessage(t('Unable to load licences. Please try again.'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setHasLoaded(true);
        }
      }
    };

    void loadLicences();
    return () => { cancelled = true; };
  }, [refreshToken]);

  const selected = useMemo(
    () => licences.find((licence) => licence.id === selectedId) ?? null,
    [licences, selectedId],
  );

  // When a licence is selected, fetch a signed URL for its document
  useEffect(() => {
    if (!selected?.document_storage_path) {
      setDocUrl(null);
      return;
    }
    let cancelled = false;
    setDocLoading(true);
    setDocUrl(null);

    supabase.storage
      .from('licence-documents')
      .createSignedUrl(selected.document_storage_path, 300) // 5-min expiry
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error('Signed URL error:', error.message);
        setDocUrl(data?.signedUrl ?? null);
        setDocLoading(false);
      });

    return () => { cancelled = true; };
  }, [selected?.id, selected?.document_storage_path]);

  const handleSelect = useCallback((licence: GuardLicence) => {
    setSelectedId(licence.id);
    setReviewerNotes(licence.reviewer_notes ?? '');
    setSaveError(null);
    setCopiedNumber(false);
  }, []);

  const closeDetail = useCallback(() => setSelectedId(null), []);

  useEffect(() => {
    setHasAppliedInitialSelection(false);
    if (initialSelectedLicenceId) setView('all');
  }, [initialSelectedLicenceId]);

  useEffect(() => {
    if (!initialSelectedLicenceId || hasAppliedInitialSelection || licences.length === 0) return;
    const matchingLicence = licences.find((licence) => licence.id === initialSelectedLicenceId);
    if (!matchingLicence) return;
    handleSelect(matchingLicence);
    setHasAppliedInitialSelection(true);
  }, [handleSelect, hasAppliedInitialSelection, initialSelectedLicenceId, licences]);

  useEffect(() => {
    if (!selectedId) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedId(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [selectedId]);

  const handleReview = async (newStatus: 'approved' | 'rejected') => {
    if (!selected) return;
    const licenceId = selected.id;
    const notes = reviewerNotes.trim() || null;
    setIsSaving(true);
    setSaveError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('guard_licences')
        .update({
          status: newStatus,
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
          reviewer_notes: notes,
        })
        .eq('id', licenceId);

      if (error) throw error;

      // Update local state
      setLicences((prev) =>
        prev.map((l) => (l.id === licenceId ? { ...l, status: newStatus, reviewer_notes: notes } : l))
      );
      // The reviewed licence has already left the pending queue; show the
      // outcome for a moment, then close so the next one can be opened.
      if (view === 'pending_review') {
        window.setTimeout(() => {
          setSelectedId((current) => (current === licenceId ? null : current));
        }, 1200);
      }
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  const copyLicenceNumber = (licenceNumber: string) => {
    navigator.clipboard.writeText(licenceNumber).then(() => {
      setCopiedNumber(true);
      setTimeout(() => setCopiedNumber(false), 2000);
    });
  };

  const handleOpenAuthorityPortal = () => {
    if (!selected) return;
    const url = buildAuthorityUrl(selected.authority);
    if (!url) return;
    if (selected.authority === 'PSA') copyLicenceNumber(selected.licence_number);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const stats = useMemo(() => {
    const counts = { pending: 0, approved: 0, expiringSoon: 0, expired: 0, rejected: 0 };
    licences.forEach((licence) => {
      switch (getEffectiveStatus(licence)) {
        case 'pending_review':
          counts.pending += 1;
          break;
        case 'approved':
          counts.approved += 1;
          if (licence.expiry_flag === 'expiring_soon') counts.expiringSoon += 1;
          break;
        case 'expired':
          counts.expired += 1;
          break;
        case 'rejected':
          counts.rejected += 1;
          break;
        default:
          break;
      }
    });
    return counts;
  }, [licences]);

  const typeNames = useMemo(() => {
    const names = new Set<string>();
    licences.forEach((licence) => {
      if (licence.licence_type_name) names.add(licence.licence_type_name);
    });
    return [...names].sort((left, right) => left.localeCompare(right, getLocale(), { sensitivity: 'base' }));
  }, [licences]);

  const matchingLicences = useMemo(() => {
    const terms = normalizeSearch(searchQuery).split(/\s+/).filter(Boolean);
    return licences
      .filter((licence) => {
        const status = getEffectiveStatus(licence);
        if (view === 'pending_review') {
          if (status !== 'pending_review') return false;
        } else if (selectedStatus && status !== selectedStatus) {
          return false;
        }
        if (selectedType && licence.licence_type_name !== selectedType) return false;
        if (selectedAuthority && licence.authority !== selectedAuthority) return false;
        if (selectedExpiry && licence.expiry_flag !== selectedExpiry) return false;
        if (terms.length === 0) return true;

        const haystack = normalizeSearch([
          licence.guard_full_name,
          licence.licence_holder_name,
          licence.licence_number,
          licence.licence_type_name,
          licence.authority,
        ].filter(Boolean).join(' '));
        return terms.every((term) => haystack.includes(term));
      })
      .sort(SORTERS[sortBy]);
  }, [licences, searchQuery, selectedAuthority, selectedExpiry, selectedStatus, selectedType, sortBy, view]);

  const isPendingView = view === 'pending_review';
  const hasActiveFilters = Boolean(
    searchQuery.trim()
    || selectedType
    || selectedAuthority
    || selectedExpiry
    || (!isPendingView && selectedStatus),
  );
  const totalCount = matchingLicences.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  // The list can shrink under the current page (a filter, a review), so the
  // page shown is clamped rather than trusted.
  const currentPage = Math.min(page, pageCount - 1);
  const pageLicences = matchingLicences.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const firstShown = totalCount === 0 ? 0 : currentPage * PAGE_SIZE + 1;
  const lastShown = Math.min((currentPage + 1) * PAGE_SIZE, totalCount);

  const statusOptions: FilterOption[] = [
    { value: '', label: t('All Statuses') },
    ...LICENCE_STATUSES.map((status) => ({ value: status, label: getStatusLabel(status) })),
  ];
  const typeOptions: FilterOption[] = [
    { value: '', label: t('All Types') },
    ...typeNames.map((name) => ({ value: name, label: name })),
  ];
  const authorityOptions: FilterOption[] = [
    { value: '', label: t('All Authorities') },
    ...LICENCE_AUTHORITIES.map((authority) => ({ value: authority, label: getAuthorityLabel(authority) })),
  ];
  const expiryOptions: FilterOption[] = [
    { value: '', label: t('Any Expiry') },
    { value: 'valid', label: t('Valid||licence') },
    { value: 'expiring_soon', label: t('Expiring Soon (30 days)') },
    { value: 'expired', label: t('Expired||licence') },
  ];
  const sortOptions: FilterOption[] = [
    { value: 'submitted_desc', label: t('Submitted (Newest)') },
    { value: 'submitted_asc', label: t('Submitted (Oldest)') },
    { value: 'expiry_asc', label: t('Expiry (Soonest)') },
    { value: 'expiry_desc', label: t('Expiry (Latest)') },
    { value: 'guard_asc', label: t('Guard (A–Z)') },
  ];

  const changeView = (nextView: LicenceView) => {
    setView(nextView);
    setPage(0);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStatus('');
    setSelectedType('');
    setSelectedAuthority('');
    setSelectedExpiry('');
    setPage(0);
  };

  if (!hasLoaded && isLoading) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  const selectedStatusValue = selected ? getEffectiveStatus(selected) : null;

  return (
    <div className="page glp" aria-busy={isLoading}>
      <div className="page__header">
        <div>
          <p className="page__eyebrow">{t('Compliance')}</p>
          <h1 className="page__title">{t('Licence Verification')}</h1>
          <p className="page__subtitle">
            {t('Review submitted licences, check them against the issuing authority and keep expiring credentials in view.')}
          </p>
        </div>
        <div className="page__header-actions">
          <button
            onClick={() => setRefreshToken((value) => value + 1)}
            className="btn btn--secondary"
            disabled={isLoading}
          >
            {isLoading ? t('Loading...') : t('Refresh')}
          </button>
        </div>
      </div>

      <p className="glp__stats-scope">{t('Counts across all licences on file')}</p>
      <div className="glp__stats">
        <div className="stat-card">
          <div className="stat-card__value glp__stat-value--warning">{stats.pending}</div>
          <div className="stat-card__label">{t('Pending Review')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__value stat-card__value--success">{stats.approved}</div>
          <div className="stat-card__label">{t('Approved||licence')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__value glp__stat-value--warning">{stats.expiringSoon}</div>
          <div className="stat-card__label">{t('Expiring Soon')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__value stat-card__value--error">{stats.expired}</div>
          <div className="stat-card__label">{t('Expired||licence')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__value glp__stat-value--muted">{stats.rejected}</div>
          <div className="stat-card__label">{t('Rejected||licence')}</div>
        </div>
      </div>

      <div className="glp__tabs">
        <button
          type="button"
          className={`glp__tab ${isPendingView ? 'glp__tab--active' : ''}`}
          aria-current={isPendingView ? 'page' : undefined}
          onClick={() => changeView('pending_review')}
        >
          {t('Pending Review')}
          {stats.pending > 0 && <span className="glp__tab-badge">{stats.pending}</span>}
        </button>
        <button
          type="button"
          className={`glp__tab ${!isPendingView ? 'glp__tab--active' : ''}`}
          aria-current={!isPendingView ? 'page' : undefined}
          onClick={() => changeView('all')}
        >
          {t('All Licences')}
        </button>
      </div>

      <div className="glp__filters panel">
        <input
          type="search"
          placeholder={t('Search guards, licence numbers or types')}
          aria-label={t('Search guards, licence numbers or types')}
          value={searchQuery}
          onChange={(event) => { setSearchQuery(event.target.value); setPage(0); }}
          className="form__input glp__filters-search"
        />
        {!isPendingView && (
          <select value={selectedStatus} onChange={(event) => { setSelectedStatus(event.target.value); setPage(0); }} className="form__select glp__filters-select" aria-label={t('Status')}>
            {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        )}
        {typeNames.length > 0 && (
          <select value={selectedType} onChange={(event) => { setSelectedType(event.target.value); setPage(0); }} className="form__select glp__filters-select" aria-label={t('Licence Type')}>
            {typeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        )}
        <select value={selectedAuthority} onChange={(event) => { setSelectedAuthority(event.target.value); setPage(0); }} className="form__select glp__filters-select" aria-label={t('Authority')}>
          {authorityOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <select value={selectedExpiry} onChange={(event) => { setSelectedExpiry(event.target.value); setPage(0); }} className="form__select glp__filters-select" aria-label={t('Expiry')}>
          {expiryOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <select value={sortBy} onChange={(event) => { setSortBy(event.target.value as LicenceSort); setPage(0); }} className="form__select glp__filters-select" aria-label={t('Sort')}>
          {sortOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>

      <div className="glp__table-shell">
        <div className="glp__table-shell-header">
          <p className="glp__showing-count">
            {isLoading
              ? t('Loading...')
              : t('Showing {first}-{last} of {total} licences', { first: firstShown, last: lastShown, total: totalCount })}
          </p>
          {hasActiveFilters && (
            <button type="button" className="glp__clear-btn" onClick={clearFilters}>{t('Clear filters')}</button>
          )}
        </div>

        {errorMessage ? (
          <div className="empty-state">
            <div className="empty-state__title">{errorMessage}</div>
            <button className="btn btn--secondary" onClick={() => setRefreshToken((value) => value + 1)}>{t('Try again')}</button>
          </div>
        ) : totalCount === 0 ? (
          <div className="empty-state">
            <div className="empty-state__title">
              {isPendingView && !hasActiveFilters ? t('No licences pending review') : t('No licences found')}
            </div>
            <div className="empty-state__sub">
              {isPendingView && !hasActiveFilters
                ? t('New submissions from your guards will appear here.')
                : t('Adjust the filters or refresh to pull the latest licence data.')}
            </div>
          </div>
        ) : (
          <div className={`glp__table-scroller${isLoading ? ' glp__table-scroller--loading' : ''}`}>
            <table className="table glp__table">
              <thead>
                <tr>
                  <th>{t('Guard')}</th>
                  <th>{t('Licence No.')}</th>
                  <th>{t('Type')}</th>
                  <th>{t('Authority')}</th>
                  <th>{t('Expiry')}</th>
                  <th>{t('Submitted')}</th>
                  {!isPendingView && <th>{t('Status')}</th>}
                </tr>
              </thead>
              <tbody>
                {pageLicences.map((l) => {
                  const status = getEffectiveStatus(l);
                  return (
                    <tr
                      key={l.id}
                      className={`glp__row ${selected?.id === l.id ? 'glp__row--active' : ''}`}
                      tabIndex={0}
                      onClick={() => handleSelect(l)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') handleSelect(l);
                      }}
                    >
                      <td><div className="glp__guard">{l.guard_full_name ?? '—'}</div></td>
                      <td className="glp__mono">{l.licence_number}</td>
                      <td>
                        {l.licence_type_name
                          ? <span className="glp__type">{l.licence_type_name}</span>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td>{getAuthorityLabel(l.authority)}</td>
                      <td>
                        <div className={`glp__expiry glp__expiry--${l.expiry_flag}`}>{formatDate(l.expiry_date)}</div>
                        {l.expiry_flag !== 'valid' && (
                          <div className="glp__expiry-note">{getExpiryTimingLabel(l.expiry_date)}</div>
                        )}
                      </td>
                      <td><span className="text-muted">{formatDate(l.submitted_at)}</span></td>
                      {!isPendingView && (
                        <td><span className={getStatusChipClass(status)}>{getStatusLabel(status)}</span></td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!errorMessage && totalCount > 0 && (
          <div className="glp__pagination">
            <button className="btn btn--secondary" onClick={() => setPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0 || isLoading}>{t('Previous')}</button>
            <span className="glp__pagination-label">{t('Page {page} of {total}', { page: currentPage + 1, total: pageCount })}</span>
            <button className="btn btn--secondary" onClick={() => setPage(Math.min(pageCount - 1, currentPage + 1))} disabled={currentPage + 1 >= pageCount || isLoading}>{t('Next')}</button>
          </div>
        )}
      </div>

      {/* Detail / Review panel */}
      {selected && selectedStatusValue && (
        <div
          className="glp__detail-overlay"
          onClick={(event) => { if (event.target === event.currentTarget) closeDetail(); }}
        >
          <aside className="glp__detail" role="dialog" aria-modal="true" aria-labelledby="glp-detail-title">
            <div className="glp__detail-header">
              <div className="glp__detail-heading">
                <p className="glp__detail-eyebrow">{t('Review Licence')}</p>
                <h2 className="glp__detail-title" id="glp-detail-title">{selected.guard_full_name ?? '—'}</h2>
                <div className="glp__detail-meta">
                  <span className={getStatusChipClass(selectedStatusValue)}>{getStatusLabel(selectedStatusValue)}</span>
                  <span className="text-muted">
                    {selected.licence_type_name ?? t('{authority} licence', { authority: selected.authority })}
                  </span>
                </div>
              </div>
              <button type="button" className="glp__close-btn" onClick={closeDetail} aria-label={t('Close')} title={t('Close')}>✕</button>
            </div>

            <div className="glp__detail-body">
              {/* Document image */}
              <div className="glp__doc-section">
                <div className="glp__section-label">{t('Licence Document')}</div>
                <div className="glp__doc-frame">
                  {docLoading ? (
                    <div className="spinner" />
                  ) : docUrl ? (
                    <a href={docUrl} target="_blank" rel="noopener noreferrer" className="glp__doc-link">
                      <img
                        src={docUrl}
                        alt={t('Licence document')}
                        className="glp__doc-img"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="glp__doc-pdf-link hidden">{t('View document (PDF)')}</div>
                    </a>
                  ) : (
                    <span className="glp__doc-empty">{t('No document attached')}</span>
                  )}
                </div>
              </div>

              {/* Licence details */}
              <div className="glp__fields">
                <div className="glp__field-row">
                  <span className="glp__field-label">{t('Licence Number')}</span>
                  <span className="glp__field-value glp__field-value--number">
                    <span className="glp__mono">{selected.licence_number}</span>
                    <button
                      type="button"
                      className="glp__copy-btn"
                      onClick={() => copyLicenceNumber(selected.licence_number)}
                      title={t('Copy licence number')}
                    >
                      {copiedNumber ? `✓ ${t('Copied')}` : t('Copy')}
                    </button>
                  </span>
                </div>
                <div className="glp__field-row">
                  <span className="glp__field-label">{t('Licence Type')}</span>
                  <span className="glp__field-value">{selected.licence_type_name ?? '—'}</span>
                </div>
                <div className="glp__field-row">
                  <span className="glp__field-label">{t('Authority')}</span>
                  <span className="glp__field-value">{getAuthorityLabel(selected.authority)}</span>
                </div>
                <div className="glp__field-row">
                  <span className="glp__field-label">{t('Country')}</span>
                  <span className="glp__field-value">{selected.country_code}</span>
                </div>
                <div className="glp__field-row">
                  <span className="glp__field-label">{t('Issue Date')}</span>
                  <span className="glp__field-value">{formatDate(selected.issue_date)}</span>
                </div>
                <div className="glp__field-row">
                  <span className="glp__field-label">{t('Expiry Date')}</span>
                  <span className="glp__field-value">
                    <span className={`glp__expiry glp__expiry--${selected.expiry_flag}`}>{formatDate(selected.expiry_date)}</span>
                    <span className="glp__field-hint">{getExpiryTimingLabel(selected.expiry_date)}</span>
                  </span>
                </div>
                <div className="glp__field-row">
                  <span className="glp__field-label">{t('Submitted')}</span>
                  <span className="glp__field-value">{formatDateTime(selected.submitted_at)}</span>
                </div>
                {selected.guard_notes && (
                  <div className="glp__field-row glp__field-row--notes">
                    <span className="glp__field-label">{t('Guard Notes')}</span>
                    <span className="glp__field-value glp__field-value--notes">{selected.guard_notes}</span>
                  </div>
                )}
              </div>

              {/* Authority portal button */}
              {buildAuthorityUrl(selected.authority) && (
                <div className="glp__authority-section">
                  <button
                    type="button"
                    className="btn btn--secondary glp__authority-btn"
                    onClick={handleOpenAuthorityPortal}
                  >
                    {t('Check on {portal}', { portal: getAuthorityPortalLabel(selected.authority) })} ↗
                  </button>
                  <p className="glp__authority-hint">
                    {selected.authority === 'PSA'
                      ? t('Copies the licence number to your clipboard and opens the PSA register — paste it into the "Licence No." field to search.')
                      : t('Opens the {authority} licence register in a new tab. Copy the licence number above to search.', { authority: selected.authority })}
                  </p>
                </div>
              )}

              {/* Review form — only for pending */}
              {selected.status === 'pending_review' && (
                <div className="glp__review-section">
                  <div className="glp__section-label">{t('Decision')}</div>
                  <label className="glp__notes-label" htmlFor="glp-reviewer-notes">{t('Reviewer Notes (optional)')}</label>
                  <textarea
                    id="glp-reviewer-notes"
                    className="form__textarea glp__notes-input"
                    value={reviewerNotes}
                    onChange={(e) => setReviewerNotes(e.target.value)}
                    placeholder={t('Add notes for the guard...')}
                    rows={3}
                  />
                  {saveError && <p className="glp__error" role="alert">{saveError}</p>}
                  <div className="glp__review-actions">
                    <button
                      type="button"
                      className="btn btn--danger"
                      onClick={() => handleReview('rejected')}
                      disabled={isSaving}
                    >
                      {isSaving ? t('Saving…') : t('Reject')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={() => handleReview('approved')}
                      disabled={isSaving}
                    >
                      {isSaving ? t('Saving…') : t('Approve')}
                    </button>
                  </div>
                </div>
              )}

              {/* Reviewed info — for already-reviewed licences */}
              {selected.status !== 'pending_review' && selected.reviewed_at && (
                <div className="glp__reviewed-info">
                  <div className="glp__section-label">{t('Review Details')}</div>
                  <div className="glp__field-row">
                    <span className="glp__field-label">{t('Reviewed by')}</span>
                    <span className="glp__field-value">{selected.reviewer_full_name ?? '—'}</span>
                  </div>
                  <div className="glp__field-row">
                    <span className="glp__field-label">{t('Reviewed at')}</span>
                    <span className="glp__field-value">{formatDateTime(selected.reviewed_at)}</span>
                  </div>
                  {selected.reviewer_notes && (
                    <div className="glp__field-row glp__field-row--notes">
                      <span className="glp__field-label">{t('Notes')}</span>
                      <span className="glp__field-value glp__field-value--notes">{selected.reviewer_notes}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
